import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Text } from '@cuemath/web-comps';
import { COLORS } from '@cuemath/comps';
import { ThumbsUp } from 'styled-icons/feather/ThumbsUp';
import { ThumbsDown } from 'styled-icons/feather/ThumbsDown';
import { getInitials, formatTime, downloadTextFile } from '../../../utils';
import { useTrialPermissions } from '../../../hooks/useTrialDBHelper';
import {
  AnnotationListWrapper,
  AnnotationItem,
  AnnotationHeader,
  AnnotationInfo,
  Avatar,
  Badge,
  EmotionIcon,
  TimeStamp,
  ActionButtons,
  ActionButton,
  AnnotationContent,
  EmptyState,
} from './AnnotationList.styles';
import { ExportButton, PanelExportHeader as ExportHeader } from './PanelShared.styles';

function AnnotationList({
  annotations = [],
  newlyCreatedAnnotation,
  onSeekToAnnotation,
  onEditAnnotation,
  onDeleteAnnotation,
  onStartAnnotation,
  isEditing = false,
  currentUserId,
  classroomId,
  demoDate,
}) {
  const { canCreateAnnotation } = useTrialPermissions();

  const handleAnnotationClick = annotation => {
    onSeekToAnnotation(annotation.timestamp.start);
  };

  const handleDeleteButtonMouseEnter = useCallback(e => {
    e.target.style.background = '#ffe8e8';
    e.target.style.borderColor = '#d32f2f';
  }, []);

  const handleDeleteButtonMouseLeave = useCallback(e => {
    e.target.style.background = '#ffffff';
    e.target.style.borderColor = '#f44336';
  }, []);

  const handleExport = () => {
    const text = annotations
      .map(a => {
        const ts =
          formatTime(a.timestamp.start) +
          (a.timestamp.end ? ` - ${formatTime(a.timestamp.end)}` : '');
        return `[${a.trialPart}] [${ts}] [${a.emotion}]\nBy: ${a.reviewerName || 'Unknown'}\n\n${a.content}`;
      })
      .join('\n\n---\n\n');
    const date = demoDate || new Date().toISOString().split('T')[0];
    downloadTextFile(`annotation-${classroomId}-${date}.txt`, text);
  };

  if (annotations.length === 0 && !isEditing) {
    return (
      <EmptyState>
        <Text variant="body1Regular" style={{ marginBottom: '8px' }}>
          No annotations yet.
        </Text>
        <Text variant="body2Regular" style={{ marginBottom: '16px' }}>
          {canCreateAnnotation
            ? 'Click "Add Note" to create one.'
            : 'You do not have permission to create annotations.'}
        </Text>
        <Button
          onClick={onStartAnnotation}
          disabled={!canCreateAnnotation}
          title={
            !canCreateAnnotation
              ? 'You do not have permission to create annotations'
              : undefined
          }
          style={{
            background: canCreateAnnotation ? COLORS.COLA : '#cccccc',
            color: COLORS.WHITE,
            padding: '8px 16px',
            fontSize: '12px',
            cursor: canCreateAnnotation ? 'pointer' : 'not-allowed',
            opacity: canCreateAnnotation ? 1 : 0.6,
          }}
        >
          ➕ Add First Annotation
        </Button>
      </EmptyState>
    );
  }

  return (
    <AnnotationListWrapper>
      <ExportHeader>
        <ExportButton onClick={handleExport} aria-label="Export annotations">&#8595; Export</ExportButton>
      </ExportHeader>
      {annotations.map(annotation => (
        <AnnotationItem
          key={annotation.annotationId}
          isNewlyCreated={newlyCreatedAnnotation === annotation.annotationId}
          onClick={() => handleAnnotationClick(annotation)}
        >
          <AnnotationHeader>
            <AnnotationInfo>
              <Avatar>{getInitials(annotation.reviewerName || 'Unknown')}</Avatar>
              <Badge>{annotation.trialPart}</Badge>
              <EmotionIcon emotion={annotation.emotion}>
                {annotation.emotion === 'positive' ? <ThumbsUp /> : <ThumbsDown />}
              </EmotionIcon>
              <TimeStamp>
                {formatTime(annotation.timestamp.start)}
                {annotation.timestamp.end && ` - ${formatTime(annotation.timestamp.end)}`}
              </TimeStamp>
            </AnnotationInfo>
            <ActionButtons>
              {currentUserId &&
                annotation.reviewerId === currentUserId &&
                canCreateAnnotation && (
                  <>
                    <ActionButton
                      onClick={e => {
                        e.stopPropagation();
                        onEditAnnotation(annotation);
                      }}
                      title="Edit annotation"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </ActionButton>
                    <ActionButton
                      onClick={e => {
                        e.stopPropagation();
                        onDeleteAnnotation(annotation.annotationId);
                      }}
                      title="Delete annotation"
                      style={{
                        background: '#ffffff',
                        color: '#f44336',
                        borderColor: '#f44336',
                      }}
                      onMouseEnter={handleDeleteButtonMouseEnter}
                      onMouseLeave={handleDeleteButtonMouseLeave}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </ActionButton>
                  </>
                )}
            </ActionButtons>
          </AnnotationHeader>
          <AnnotationContent variant="body2Regular">
            {annotation.content}
          </AnnotationContent>
        </AnnotationItem>
      ))}
    </AnnotationListWrapper>
  );
}

AnnotationList.propTypes = {
  annotations: PropTypes.arrayOf(
    PropTypes.shape({
      annotationId: PropTypes.string.isRequired,
      trialId: PropTypes.string,
      reviewerId: PropTypes.string,
      reviewerName: PropTypes.string,
      trialPart: PropTypes.string.isRequired,
      emotion: PropTypes.string.isRequired,
      timestamp: PropTypes.shape({
        start: PropTypes.number.isRequired,
        end: PropTypes.number,
      }).isRequired,
      content: PropTypes.string.isRequired,
      createdAt: PropTypes.string,
      updatedAt: PropTypes.string,
    }),
  ),
  newlyCreatedAnnotation: PropTypes.string,
  onSeekToAnnotation: PropTypes.func.isRequired,
  onEditAnnotation: PropTypes.func.isRequired,
  onDeleteAnnotation: PropTypes.func.isRequired,
  onStartAnnotation: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
  currentUserId: PropTypes.string,
  classroomId: PropTypes.string,
  demoDate: PropTypes.string,
};

AnnotationList.defaultProps = {
  annotations: [],
  newlyCreatedAnnotation: null,
  classroomId: '',
  demoDate: '',
};

export default AnnotationList;
